<template>
  <div
    :class="entityClasses"
    :style="entityStyle"
    @click="maybeExpand"
  >
    <div class="entity__people">
      <template
        v-for="(person, index) in people"
      >
        <Person
          v-if="person.id"
          :key="person.id"
          :class="['entity__person', { 'entity__person--additional': index }]"
          :style="peopleStyles[index]"
          :size="avatarSize"
          v-bind="person"
        />
      </template>
    </div>
    <div :class="['entity__balance', { 'entity__balance--owing': isOwing }]">
      {{Math.round(balance) * -1}}
    </div>
  </div>
</template>

<script>
import { AVATAR_SIZES } from '../constants.js'
import Person from './Person.vue';

export default {
  name: 'Entity',
  components: {
    Person,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    people: {
      type: Array,
      required: true,
    },
    balance: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      avatarSize: AVATAR_SIZES.LARGE,
      isExpanded: false,
    };
  },
  computed: {
    isOwing() {
      return this.balance > 0;
    },
    entityClasses() {
      return [
        'entity',
        {
          'entity--expanded': this.isExpanded,
        },
      ];
    },
    expandedOffset() {
      const { $el } = this;
      const viewportCenter = window.innerWidth / 2;
      const { x } = $el.getBoundingClientRect();
      return viewportCenter - x;
    },
    entityStyle() {
      if (!this.isExpanded) {
        return {};
      }
      return {
        transform: `translateX(calc(${this.expandedOffset}px - 50%))`,
      };
    },
    expandedPeopleOffsets() {
      const { people, avatarSize } = this;
      const overlap = avatarSize  / 2;
      const middleIndex = (people.length - 1) / 2;
      const centralOffset = people.length % 2
        ? 0
        // if odd number of people - central avatar doesn't move
        : overlap / 2;
        // if even - two central avatars move halfway
      return people.map((person, index) => {
        if (index === middleIndex) {
          return 0;
        }
        const direction = index < middleIndex ? 1 : -1;
        const multiplier = Math.floor(Math.abs(index - middleIndex));
        return direction * (centralOffset + overlap * multiplier);
      });
    },
    peopleStyles() {
      const { people, expandedPeopleOffsets, isExpanded } = this;
      return people.map((person, index) => {
        if (!isExpanded) {
          return {};
        }
        return {
          transform: `translateX(${expandedPeopleOffsets[index]}px)`,
        };
      });
    },
  },
  methods: {
    maybeExpand() {
      if (this.isExpanded) {
        return;
      }
      this.isExpanded = true;
      this.$el.addEventListener('transitionend', () => {
        document.addEventListener('click', this.shrink);
      }, {once: true});
    },
    shrink() {
      this.isExpanded = false;
      document.removeEventListener('click', this.shrink)
    }
  },
  beforeDestroy() {
    this.shrink();
  },
}
</script>

<style scoped>
.entity {
  transition: transform .2s ease-in-out;
  position: relative;
  z-index: 1;
  cursor: pointer;
  padding: 1em;
}
.entity::before {
  content: '';
  display: block;
  width: 100vw;
  position: absolute;
  z-index: -1;
  top: 0;
  bottom: 0;
  left: 50%;
  background: #fff;
  opacity: 0;
  transition: opacity .2s ease-in-out;
  margin-left: -50vw;
  pointer-events: none;
}
.entity--expanded {
  z-index: 2;
}
.entity--expanded::before {
  opacity: 1;
  pointer-events: auto;
}
.entity__people {
  padding: 10px;
  display: flex;
  flex-direction: row-reverse;
}
.entity__person {
  transition: transform ease-in-out .2s;
}
.entity__person:not(:last-child) {
  margin-left: -30px;
}
.entity__balance {
  text-align: center;
}
.entity__balance--owing {
  color: red;
}
</style>
